/**
 * نظام الصلاحيات المتكامل - النسخة الجديدة
 * يعتمد على الصلاحيات الفعلية للمستخدم بدلاً من أسماء الأدوار
 * يستخدم Zustand كمخزن مركزي للصلاحيات
 */

// إزالة استيراد خدمة الكوكيز - سنعتمد على sessionStorage فقط

// أنواع الأدوار المتاحة في النظام - محتفظ بها للتوافق الخلفي
// ملاحظة: سيتم إزالة هذه الثوابت تدريجياً واستبدالها بنظام الصلاحيات الجديد
export const ROLES = {
  ADMIN: 'admin',
  SUPERVISOR: 'supervisor',
  INSTRUCTOR: 'instructor',
  TRAINEE: 'trainee',
  BLOCKED: 'blocked'
};

// دعم الأسماء العربية والإنجليزية للأدوار - محتفظ بها للتوافق الخلفي
// هذه الوظيفة تتحقق من نوع الدور بغض النظر عن الاسم
export function checkRoleType(roleName) {
  // التحقق من المطابقة المباشرة أولاً
  if (roleName === ROLES.ADMIN) return 'ADMIN';
  if (roleName === ROLES.SUPERVISOR) return 'SUPERVISOR';
  if (roleName === ROLES.INSTRUCTOR) return 'INSTRUCTOR';
  if (roleName === ROLES.TRAINEE) return 'TRAINEE';
  if (roleName === ROLES.BLOCKED) return 'BLOCKED';
  
  // إذا لم يوجد تطابق دقيق، نتحقق من الأسماء العربية
  const lowerCaseName = roleName?.toLowerCase();
  
  // دعم الأسماء العربية
  if (roleName === 'مشرف' || lowerCaseName?.includes('supervisor')) return 'SUPERVISOR';
  if (roleName === 'مدرب' || lowerCaseName?.includes('instructor')) return 'INSTRUCTOR';
  if (roleName === 'متدرب' || lowerCaseName?.includes('trainee')) return 'TRAINEE';
  if (roleName === 'محظور' || lowerCaseName?.includes('block')) return 'BLOCKED';
  if (roleName === 'ادمن' || roleName === 'مدير' || lowerCaseName?.includes('admin')) return 'ADMIN';
  
  // إذا لم يتم التعرف على الدور
  return null;
}

// تعريف الصلاحيات لكل دور
// الصلاحيات بنفس الأسماء الموجودة في الباك إند
const ROLE_PERMISSIONS = {
  admin: [
    // الدورات (Courses)
    'view_courses',
    'create_course',
    'edit_course',
    'delete_course',
    'view_course_content',
    'create_course_content',
    'edit_course_content',
    'delete_course_content',
    'view_exams',
    'create_exam',
    'edit_exam',
    'delete_exam',
    
    // المستخدمين (Users)
    'view_admins',
    'create_admin',
    'edit_admin',
    'delete_admin',
    'view_instructors',
    'create_instructor',
    'edit_instructor',
    'delete_instructor',
    'view_trainees',
    'create_trainee',
    'edit_trainee',
    'delete_trainee',
    
    // الأجهزة (Devices)
    'view_devices',
    'manage_devices',
    'delete_devices',
    
    // المدفوعات (Payments)
    'view_bank_transfers',
    'verify_bank_transfers',
    'delete_bank_transfers',
    'view_electronic_payments',
    'manage_electronic_payments',
    'delete_electronic_payments',
    
    // الخصومات (Discounts)
    'view_coupons',
    'create_coupon',
    'edit_coupon',
    'delete_coupon',
    'view_course_discounts',
    'create_course_discount',
    'edit_course_discount',
    'delete_course_discount',
    
    // الكتب (Books)
    'view_books',
    'create_book',
    'edit_book',
    'delete_book',
    
    // التقييمات (Reviews)
    'view_reviews',
    'create_review',
    'edit_review',
    'delete_review',
    
    // الكلمات المفتاحية (Keywords)
    'view_keywords',
    'create_keyword',
    'edit_keyword',
    'delete_keyword',
    
    // الإعدادات (Settings)
    'view_settings',
    'manage_settings'
  ],
  
  supervisor: [
    // الدورات
    'view_courses',
    'create_course',
    'edit_course',
    'view_course_content',
    'create_course_content',
    'edit_course_content',
    'view_exams',
    'create_exam',
    'edit_exam',
    'delete_exam',

    // المستخدمين
    'view_instructors',
    'create_instructor',
    'edit_instructor',
    'delete_instructor',
    'view_trainees',
    'create_trainee',
    'edit_trainee',
    'delete_trainee',

    // الأجهزة
    'view_devices',
    'manage_devices',

    // المدفوعات
    'view_bank_transfers',
    'verify_bank_transfers',
    
    // الخصومات
    'view_coupons',
    'create_coupon',
    'edit_coupon',
    
    // الكلمات المفتاحية
    'view_keywords',
    'create_keyword',
    'edit_keyword',
    
    // الإعدادات
    'view_settings'
  ],
  
  instructor: [
    // الدورات والمحتوى التعليمي
    'view_courses',
    'view_course_content',
    'create_course_content',
    'edit_course_content',
    'view_exams',
    'create_exam',
    'edit_exam',
    
    // التقييمات
    'view_reviews',
    'create_review',
    'edit_review',
    
    // المتدربين
    'view_trainees'
  ],
  
  trainee: [
    // الدورات والمحتوى التعليمي
    'view_courses',
    'view_course_content',
    'view_exams',
    'view_books',
    'view_reviews',
    'create_review',
    'view_keywords'
  ],
  
  blocked: []
};

/**
 * التحقق من وجود صلاحية محددة لدور معين
 * @param {string} role دور المستخدم (admin, supervisor, instructor, trainee, blocked)
 * @param {string} permission الصلاحية المطلوب التحقق منها
 * @returns {boolean} نتيجة التحقق
 */
export function checkPermission(role, permission) {
  if (!role || !permission) return false;
  return ROLE_PERMISSIONS[role]?.includes(permission) || false;
}

/**
 * التحقق من وجود صلاحية محددة للمستخدم
 * @param {string} permission اسم الصلاحية المطلوب التحقق منها
 * @returns {boolean} ما إذا كان المستخدم يملك الصلاحية أم لا
 */
export const hasPermission = (permission) => {
  // تم إزالة سجل بداية التحقق من الصلاحية لتنظيف الكونسول
  
  // التحقق من نوع المستخدم (إذا كان مستخدم عادي، نستخدم قائمة ثابتة من الصلاحيات)
  // نعتمد على sessionStorage فقط للحصول على معرف الدور
  const roleId = sessionStorage.getItem('userRoleId');
  // تم إزالة سجل معرف دور المستخدم لتنظيف الكونسول
  
  // المستخدمين العاديين (roleId = 4) لهم صلاحيات محددة مسبقاً
  if (roleId === '4') {
    // قائمة ثابتة من الصلاحيات المسموحة للمستخدم العادي
    const basicUserPermissions = ['view_courses', 'create_review', 'view_profile'];
    return basicUserPermissions.includes(permission);
  }
  
  // لا نستخدم استدعاء مباشر لل Redux لتجنب مشاكل التحديث غير المقصود
  // بدلاً من ذلك، نعتمد فقط على sessionStorage لاستدعاء الصلاحيات
  
  // إذا كان Redux فارغاً، نتحقق من sessionStorage مباشرة
  try {
    // التحقق مباشرة من sessionStorage
    const sessionPermissions = JSON.parse(sessionStorage.getItem('userPermissions') || '[]');
    
    if (Array.isArray(sessionPermissions) && sessionPermissions.length > 0) {
      const hasPermissionInSession = sessionPermissions.includes(permission);
      return hasPermissionInSession;
    }
    
    // إذا وصلنا هنا، فهذا يعني أننا لم نجد الصلاحية في أي مكان
    return false;
  } catch (error) {
    console.error('خطأ في قراءة الصلاحيات:', error);
    return false;
  }
};

/**
 * الحصول على صلاحيات المستخدم الحالي
 * @returns {Array} مصفوفة تحتوي على أسماء الصلاحيات
 */
export const getUserPermissions = () => {
  // استخدام Redux بدلاً من localStorage للأمان
  const store = require('../store/store').default;
  const state = store.getState();
  return Array.isArray(state.permissions.permissions) ? state.permissions.permissions : [];
};

/**
 * دوال مساعدة للتحقق من أنواع معينة من الصلاحيات
 */
export const Permissions = {
  // الدورات
  canViewCourse: () => hasPermission('view_courses'),
  canCreateCourse: () => hasPermission('create_course'),
  canEditCourse: () => hasPermission('edit_course'),
  canDeleteCourse: () => hasPermission('delete_course'),
  
  // محتوى الدورات
  canViewCourseContent: () => hasPermission('view_course_content'),
  canCreateCourseContent: () => hasPermission('create_course_content'),
  canEditCourseContent: () => hasPermission('edit_course_content'),
  canDeleteCourseContent: () => hasPermission('delete_course_content'),
  
  // الاختبارات
  canViewExams: () => hasPermission('view_exams'),
  canCreateExam: () => hasPermission('create_exam'),
  canEditExam: () => hasPermission('edit_exam'),
  canDeleteExam: () => hasPermission('delete_exam'),
  
  // المستخدمين
  canViewAdmins: () => hasPermission('view_admins'),
  canViewInstructors: () => hasPermission('view_instructors'),
  canViewTrainees: () => hasPermission('view_trainees'),
  canManageUsers: () => hasPermission('view_admins') || hasPermission('view_instructors') || hasPermission('view_trainees'),
  
  // الأجهزة
  canViewDevices: () => hasPermission('view_devices'),
  canManageDevices: () => hasPermission('manage_devices'),
  
  // المدفوعات
  canViewBankTransfers: () => hasPermission('view_bank_transfers'),
  canVerifyBankTransfers: () => hasPermission('verify_bank_transfers'),
  
  // الخصومات
  canViewCoupons: () => hasPermission('view_coupons'),
  canManageCoupons: () => hasPermission('create_coupon') || hasPermission('edit_coupon') || hasPermission('delete_coupon'),
  
  // الكتب
  canViewBooks: () => hasPermission('view_books'),
  canManageBooks: () => hasPermission('create_book') || hasPermission('edit_book') || hasPermission('delete_book'),
  
  // التقييمات
  canViewReviews: () => hasPermission('view_reviews'),
  canCreateReview: () => hasPermission('create_review'),
  
  // الكلمات المفتاحية
  canViewKeywords: () => hasPermission('view_keywords'),
  canManageKeywords: () => hasPermission('create_keyword') || hasPermission('edit_keyword') || hasPermission('delete_keyword'),
  
  // الإعدادات
  canViewSettings: () => hasPermission('view_settings'),
  canManageSettings: () => hasPermission('manage_settings'),
  
  // لوحة التحكم - التحقق من صلاحية الدخول للوحة التحكم
  canAccessAdminDashboard: () => hasPermission('access_dashboard')
};

export default {
  hasPermission,
  getUserPermissions,
  Permissions,
  checkRoleType,
  ROLES
};
